"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { EnterpriseDemoVideo } from "@/components/enterprise/demo-video";
import { Button } from "@/lib/components/button";
import { NumberTicker } from "@/lib/components/number-ticker";
interface StatCardProps {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  delay?: number;
}
function StatCard(t0) {
  const $ = _c(12);
  const {
    value,
    suffix: t1,
    prefix: t2,
    label,
    delay
  } = t0;
  const suffix = t1 === undefined ? "" : t1;
  const prefix = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== delay || $[1] !== value) {
    t3 = <NumberTicker value={value} delay={delay} className="text-primary" />;
    $[0] = delay;
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== prefix || $[4] !== suffix || $[5] !== t3) {
    t4 = <div className="text-2xl sm:text-3xl font-bold text-primary">{prefix}{t3}{suffix}</div>;
    $[3] = prefix;
    $[4] = suffix;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== label) {
    t5 = <span className="mt-2 text-sm text-muted-foreground font-medium">{label}</span>;
    $[7] = label;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex flex-col items-center p-6 rounded-2xl border border-border bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function toTickerStat(n: number): {
  value: number;
  suffix: string;
} {
  if (n >= 1_000_000_000) {
    return {
      value: Math.floor(n / 1_000_000_000),
      suffix: "B+"
    };
  }
  if (n >= 1_000_000) {
    return {
      value: Math.floor(n / 1_000_000),
      suffix: "M+"
    };
  }
  if (n >= 1_000) {
    return {
      value: Math.floor(n / 1_000),
      suffix: "K+"
    };
  }
  return {
    value: n,
    suffix: ""
  };
}
interface HeroEnterpriseProps {
  totalTokens?: number;
  totalRequests?: number;
}
export function HeroEnterprise(t0) {
  const $ = _c(23);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    totalTokens: t2,
    totalRequests: t3
  } = t1;
  const totalTokens = t2 === undefined ? 100000000000 : t2;
  const totalRequests = t3 === undefined ? 20000000 : t3;
  let t4;
  if ($[2] !== totalTokens) {
    t4 = toTickerStat(totalTokens);
    $[2] = totalTokens;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const tokensStat = t4;
  let t5;
  if ($[4] !== totalRequests) {
    t5 = toTickerStat(totalRequests);
    $[4] = totalRequests;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const requestsStat = t5;
  let t6;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Link href="/blog/soc2-type-ii" className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-4 py-1.5 transition-colors hover:border-blue-500/50"><span className="text-xs font-mono text-blue-500">ENTERPRISE</span><span className="text-xs text-muted-foreground">SOC 2 Type II certified</span></Link>;
    t7 = <h1 className="mb-6 text-4xl font-bold tracking-tight text-balance sm:text-6xl lg:text-7xl">Enterprise LLM Gateway for mission-critical applications</h1>;
    t8 = <p className="mb-10 text-lg text-muted-foreground text-balance sm:text-xl max-w-3xl mx-auto leading-relaxed">Deploy a fully-managed or self-hosted LLM gateway with enterprise SSO, white-labeling, and infrastructure-as-code support for your cloud or bare metal infrastructure.</p>;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Button size="lg" className="w-full sm:w-auto" asChild={true}><Link href="/enterprise#contact">Contact Us<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mx-auto max-w-4xl text-center">{t6}{t7}{t8}<div className="flex flex-col sm:flex-row items-center justify-center gap-4">{t9}<Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent" asChild={true}><Link href="/signup">Explore The Product</Link></Button></div><p className="mt-5 text-sm text-muted-foreground">Every enterprise plan starts with the 30-Day Production Pilot — live traffic in week one, a decision gate at day 30.</p></div>;
    t11 = <EnterpriseDemoVideo />;
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  let t12;
  if ($[12] !== tokensStat.suffix || $[13] !== tokensStat.value) {
    t12 = <StatCard value={tokensStat.value} suffix={tokensStat.suffix} label="Total Tokens Processed" />;
    $[12] = tokensStat.suffix;
    $[13] = tokensStat.value;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== requestsStat.suffix || $[16] !== requestsStat.value) {
    t13 = <StatCard value={requestsStat.value} suffix={requestsStat.suffix} label="Total Requests" delay={0.1} />;
    $[15] = requestsStat.suffix;
    $[16] = requestsStat.value;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <StatCard value={200} suffix="M" label="Daily Tokens" delay={0.2} />;
    t15 = <StatCard value={80} suffix="K" prefix="$" label="Customer Savings" delay={0.3} />;
    $[18] = t14;
    $[19] = t15;
  } else {
    t14 = $[18];
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t12 || $[21] !== t13) {
    t16 = <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28"><div className="container mx-auto px-4 sm:px-6 lg:px-8">{t10}{t11}<div className="mx-auto mt-16 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-6">{t12}{t13}{t14}{t15}</div></div></section>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  return t16;
}